import { connect, useDispatch } from 'react-redux';
import { Form, FormRenderProps } from 'react-final-form';
import { Dispatch, useCallback, useEffect, useState } from 'react';
import arrayMutators from 'final-form-arrays';
import { v4 as uuidv4 } from 'uuid';

import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { Modal } from '../../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { getCatalogKnmRemarkSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmRemarkSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmRemarkSelectOption';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import styles from './KnmListByPlanApproveRemarksModal.module.scss';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { ErknmApproveRemarkSectionCodeEnum } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IKnmApproveRemark248 } from '../../../APIPackage/KnmModule/interfaces/IKnmApproveRemark248';
import { TableCheckItems } from '../../../CorePackage/MainModule/types/TableCheckItems';
import { IKnmErknmFormApproveRemark } from '../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormApproveRemark';
import { approveRemarksToNote } from '../../../KnmErknmPackage/ErknmApproveRemarkModule/common/approveRemarksToNote';
import { knmListByPlanApproveRemarks } from '../actions/knmListByPlanActions';

interface IKnmListByPlanApproveRemarksModalProps {
  visible: boolean;
  onClose: () => void;
  itemsCheck: TableCheckItems;
}

interface IKnmListByPlanApproveRemarksModalStoreProps {
  knmRemarkSelectOptions: IKnmRemarkSelectOption[];
}

interface IApproveRemarksForm {
  remarks?: IKnmErknmFormApproveRemark[];
  emptyRemark?: IKnmRemarkSelectOption;
}

const fields = [
  {
    label: 'Вид КНМ',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.KIND_KNM,
  },
  {
    label: 'Даты проведения КНМ',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.DATES,
  },
  {
    label: 'Основание включения в план',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.REASONS,
  },
  {
    label: 'Сведения об объектах контроля',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.OBJECTS,
  },
  {
    label: 'Органа контроля, с которым КНМ проводится совместно',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.COLLABORATING_ORGANIZATIONS,
  },
  {
    label: 'Перечень действий, осуществляемых в рамках КНМ',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.EVENTS,
  },
  {
    label: 'Обязательные требования, подлежащие проверке',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.REQUIREMENTS,
  },
  {
    label: 'Проверочные листы',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.CHECKLISTS,
  },
  {
    label: 'Место (места) проведения контрольного (надзорного) мероприятия',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.PLACES,
  },
  {
    label: 'Разрешительные и иные документы',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.APPROVE_DOCS,
  },
  {
    label:
      'Сведения об экспертах, экспертных организациях, специалистах, независимых органах инспекции, саморегулируемых организациях и иных лицах, привлекаемых для проведения КНМ',
    sectionCode: ErknmApproveRemarkSectionCodeEnum.EXPERTS,
  },
];

const KnmListByPlanApproveRemarksModalWithoutStore = ({
  visible,
  onClose,
  knmRemarkSelectOptions,
  itemsCheck,
}: IKnmListByPlanApproveRemarksModalProps & IKnmListByPlanApproveRemarksModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.KNM_REMARKS));
  }, []);

  const [selectedSections, setSelectedSection] = useState<ErknmApproveRemarkSectionCodeEnum[]>([]);

  const isSectionSelected = useCallback(
    (sectionCode: ErknmApproveRemarkSectionCodeEnum) => selectedSections.includes(sectionCode),
    [selectedSections],
  );

  const closeHandle = useCallback(() => {
    setSelectedSection([]);
    onClose();
  }, [onClose, setSelectedSection]);

  const onSubmit = useCallback(
    (values: IApproveRemarksForm) => {
      const knmIds = Object.keys(itemsCheck)
        .filter(item => itemsCheck[item])
        .map(item => +item);
      const remarks: IKnmApproveRemark248[] =
        values.remarks?.map(item => ({
          guid: null,
          section: item.sectionCode,
          remarkId: typeof item.remark?.id === 'number' ? item.remark.id : null,
          description: item.description || null,
          changes: null,
        })) || [];
      const note = approveRemarksToNote(values.remarks);

      dispatch(knmListByPlanApproveRemarks(knmIds, remarks, note));
      closeHandle();
    },
    [onClose, itemsCheck, dispatch],
  );

  const renderForm = ({ values, form, handleSubmit }: FormRenderProps<IApproveRemarksForm>) => {
    const { remove, push } = form.mutators;

    const checkboxHandle = (sectionCode: ErknmApproveRemarkSectionCodeEnum) => {
      if (isSectionSelected(sectionCode)) {
        setSelectedSection(selectedSections.filter(item => item !== sectionCode));
        const formIndex = values.remarks?.findIndex(item => item.sectionCode === sectionCode);

        if (formIndex !== -1) {
          remove('remarks' as keyof IApproveRemarksForm, formIndex);
        }
      } else {
        setSelectedSection([...selectedSections, sectionCode]);
        const newItem: IKnmErknmFormApproveRemark = {
          listId: uuidv4(),
          sectionCode,
        };

        push('remarks' as keyof IApproveRemarksForm, newItem);
      }
    };

    return (
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className={styles.Header}>
            <div></div>
            <div>Раздел паспорта</div>
            <div>Замечание</div>
          </div>
          <div className={styles.Body}>
            {fields.map((field, index) => {
              const isSelected = isSectionSelected(field.sectionCode);
              const formIndex = values.remarks?.findIndex(item => item.sectionCode === field.sectionCode);
              const isFormIndexFound = formIndex !== undefined && formIndex !== -1;
              const remarkFieldName = isFormIndexFound ? `remarks[${formIndex}].remark` : 'emptyRemark';
              const descriptionFieldName = isFormIndexFound ? `remarks[${formIndex}].description` : 'emptyDescription';
              const selectOptions = knmRemarkSelectOptions.filter(item => item.sectionCode === field.sectionCode);

              return (
                <div key={index} className={styles.Row}>
                  <Checkbox
                    theme="system"
                    type="checkbox"
                    onChange={() => checkboxHandle(field.sectionCode)}
                    checked={isSelected}
                  />
                  <div className={styles.Label}>{field.label}</div>
                  <div className={styles.FieldsBlock}>
                    <SelectInputField
                      name={remarkFieldName}
                      validate={makeRequired(REQUIRED_ERROR, () => !isFormIndexFound)}
                      disabled={!isSelected}
                      options={selectOptions}
                      placeholder="Выберите замечание"
                    />
                    {isSelected && (
                      <TextInputField
                        name={descriptionFieldName}
                        placeholder="Введите подробности (при необходимости)"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ModalBody>
        <ModalActions>
          <Button text="Сохранить" size="medium" color="primary" type="submit" disabled={!selectedSections.length} />
          <Button text="Отмена" size="medium" type="button" onClick={closeHandle} />
        </ModalActions>
      </form>
    );
  };

  return (
    <Modal visible={visible} onClose={closeHandle}>
      <ModalHeader sizeText="medium" title="Замечания для выбранных КНМ" />
      <Form<IApproveRemarksForm> render={renderForm} onSubmit={onSubmit} mutators={{ ...arrayMutators }} />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IKnmListByPlanApproveRemarksModalStoreProps => ({
  knmRemarkSelectOptions: getCatalogKnmRemarkSelectOptions(state),
});

export const KnmListByPlanApproveRemarksModal = connect(mapStateToProps)(KnmListByPlanApproveRemarksModalWithoutStore);
